import { useId, useState } from "react";
import { Link } from "wouter";
import { m } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { ArrowRight, Clock, Heart, Link2, PhoneCall, Plus, Share2, ShieldAlert } from "lucide-react";
import { useCurrentUser } from "../lib/auth/useAuth";
import { useChildren, type ChildSummary } from "../lib/api/useChildren";
import { useMyOrgs } from "../lib/api/useOrganizations";
import { useDashboardOverview } from "../lib/api/useDashboardOverview";
import { useNotifications } from "../lib/api/useNotifications";
import { ORG_SUBJECT_NOUN } from "@shared/constants/orgRoles";
import { AppShell } from "../components/AppShell";
import { PlayfulHero } from "../components/PlayfulHero";
import { StatRow, type StatTileId } from "../components/dashboard/StatRow";
import { StatDetailDialog } from "../components/dashboard/StatDetailDialog";
import { ActiveLinksDialog } from "../components/dashboard/ActiveLinksDialog";
import { RecentActivity } from "../components/dashboard/RecentActivity";
import { QuickActions } from "../components/dashboard/QuickActions";
import { ChildAvatar } from "../components/ui/ChildAvatar";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Skeleton } from "../components/ui/Skeleton";
import { IconChip } from "../components/ui/IconChip";
import { Sparkle, Star } from "../components/decor";
import { ACCENTS, accentForIndex, type AccentName } from "../lib/accents";
import { fadeUp, staggerContainer } from "../lib/motion";
import { cn } from "../lib/cn";
import { FOCUS_RING } from "../lib/focusRing";
import { ROW } from "../lib/surfaces";
import { childProfileHref, childShareHref } from "../lib/childLinks";
import { FeatureSlot } from "../features/_sim/Slot"; // SIM-SEAM: v2 dashboard slots
import { useSlotHasEntries } from "../features/_sim/registry"; // SIM-SEAM: "More for you" heading

export function Dashboard() {
  const { data: user } = useCurrentUser();
  const { data: orgs } = useMyOrgs();
  const { data: children, isLoading, error } = useChildren();
  // Independent of the profile grid — a slow/failed overview never blocks it.
  const { data: overview, isLoading: overviewLoading } = useDashboardOverview();
  // Shared ["notifications"] query (also drives the header bell): feeds the
  // Alerts tile here; the inline RecentActivity panel reads the same cache.
  const { data: notifications, isLoading: notificationsLoading } = useNotifications();
  const firstName = user?.fullName ? user.fullName.split(" ")[0] : "";
  const profilesHeadingId = useId();
  // Which stat tile's detail modal is open. Owned here rather than in StatRow so
  // that component stays presentational — it reports a click, nothing more.
  const [openTile, setOpenTile] = useState<StatTileId | null>(null);

  const currentOrg = orgs?.find((o) => o.id === user?.currentOrganizationId) ?? null;
  const noun = currentOrg ? ORG_SUBJECT_NOUN[currentOrg.kind] : "child";
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);
  const addLabel = `Add ${noun}`;

  // A non-nullable list so `hasProfiles`/`isEmpty` gate the render without TS
  // re-narrowing `children` in each JSX branch.
  const childList = children ?? [];
  const hasProfiles = !isLoading && !error && childList.length > 0;
  const isEmpty = !isLoading && !error && !!children && childList.length === 0;

  return (
    <AppShell maxWidth="5xl">
      <PlayfulHero
        accent="sky"
        decor="full"
        eyebrow={currentOrg ? "Organization" : "Dashboard"}
        title={
          currentOrg
            ? currentOrg.name
            : `${isEmpty ? "Welcome" : "Welcome back"}${firstName ? `, ${firstName}` : ""}!`
        }
        description={
          currentOrg
            ? `Manage your organization's ${noun} records and share them securely.`
            : "Manage your children's care profiles and share them securely."
        }
        // A fresh account already gets "Add your first child" in Getting
        // started and in the empty state just below; a third button up here
        // would only crowd the first screen. Shown once profiles have loaded,
        // so a fresh account never sees it flash in and out.
        actions={
          hasProfiles ? (
            <Button asChild>
              <Link href="/child/new">
                <Plus className="h-4 w-4" aria-hidden />
                {addLabel}
              </Link>
            </Button>
          ) : undefined
        }
      />
      {/* SIM-SEAM: Getting started */}
      <FeatureSlot name="dashboard.top" className="mt-8" />

      {/* The essentials first ("very product-centric"): quick actions for
          everyone, then the at-a-glance tiles and recent activity once there's
          a profile — a fresh account never sees empty tiles. Quick actions
          wait for the profiles (sharing and "Add child" need one), so the
          tiles never reshuffle as the list arrives. */}
      {!isLoading && (
        <m.div variants={fadeUp} initial="hidden" animate="show" className="mt-8 sm:mt-10">
          <QuickActions profiles={childList} addLabel={addLabel} showOrgAdmin={Boolean(currentOrg)} />
        </m.div>
      )}

      {hasProfiles && (
        // The stat tiles stand as a narrow "at a glance" column beside the
        // activity feed on wide screens (two tiles stretched across 1500px
        // read as empty boxes); on phones and tablets they sit side by side
        // above it. grid-cols-1 is minmax(0, 1fr): without it the implicit
        // column grows to the notification titles' full width on a phone.
        // On a wide screen the tiles share the feed's height, so the column
        // and the panel end on one line.
        <m.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="mt-10 grid grid-cols-1 gap-4 sm:mt-12 sm:gap-6 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]"
        >
          <StatRow
            activeLinkCount={overview?.activeLinkCount}
            unreadCount={notifications?.unreadCount}
            medicationsExpiringSoon={overview?.medicationsExpiringSoon}
            nextMedicationExpiryDays={overview?.nextMedicationExpiryDays}
            overviewLoading={overviewLoading}
            notificationsLoading={notificationsLoading}
            onSelect={setOpenTile}
            className="lg:auto-rows-fr lg:grid-cols-1"
          />
          {/* data-tour: the welcome tour's "Recent activity & alerts" stop. */}
          <div data-tour="recent-activity" className="min-w-0 rounded-[2rem]">
            {/* h-full: the card ends on the same line as the tiles beside it. */}
            <RecentActivity className="h-full" />
          </div>
        </m.div>
      )}

      {/* SIM-SEAM: the PH season card — an alert, so it follows Recent activity & alerts */}
      <FeatureSlot name="dashboard.alerts" className="mt-10 space-y-6 sm:mt-12" />

      {/* data-tour: the welcome tour's "Child profiles" stop. */}
      <section aria-labelledby={profilesHeadingId} data-tour="child-profiles" className="mt-12 sm:mt-14">
        <h2
          id={profilesHeadingId}
          className="font-display text-xl font-semibold leading-tight text-neutral-900 sm:text-[1.4rem]"
        >
          {Noun} profiles
        </h2>

        {isLoading && <SkeletonGrid />}

        {error && (
          <p className="mt-6 rounded-2xl bg-red-50 p-4 text-sm text-red-700">{error.message}</p>
        )}

        {isEmpty && <NoProfiles noun={noun} />}

        {hasProfiles && (
          <m.ul
            variants={staggerContainer}
            initial="hidden"
            animate="show"
            className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {childList.map((child, i) => (
              <ChildCard key={child.id} child={child} index={i} />
            ))}
          </m.ul>
        )}
      </section>

      {/* SIM-SEAM: daily wins (switched off) — more to explore, before "More for you" */}
      <FeatureSlot name="dashboard.main" className="mt-12 space-y-6 sm:mt-14" />
      <MoreForYou />
      {/* SIM-SEAM: at most one partner unit */}
      <FeatureSlot name="dashboard.footer" className="mt-10" />
      {/* The Shared care links tile opens its own popup (cancel / delete links);
          the other tiles share the read-only detail dialog. */}
      <ActiveLinksDialog
        open={openTile === "links"}
        onOpenChange={(open) => !open && setOpenTile(null)}
      />
      <StatDetailDialog
        tile={openTile === "links" ? null : openTile}
        onClose={() => setOpenTile(null)}
      />
    </AppShell>
  );
}

/**
 * "More for you" (slot dashboard.content): the community's newest posts, a
 * family story and a best-practice guide — more to explore below the
 * essentials. The heading shows only when something renders under it: not at
 * all when no enabled feature registers into the slot, and hidden by CSS when
 * every entry rendered nothing (the slot's wrapper is then `:empty`). Entries
 * that mark themselves `data-more-card` (the story and the guide) share a row
 * on a wide screen; everything else takes the full row.
 */
function MoreForYou() {
  const headingId = useId();
  const hasEntries = useSlotHasEntries("dashboard.content");
  if (!hasEntries) return null;
  return (
    <section aria-labelledby={headingId} className="mt-12 has-[>div:empty]:hidden sm:mt-14">
      <h2
        id={headingId}
        className="font-display text-xl font-semibold leading-tight text-neutral-900 sm:text-[1.4rem]"
      >
        More for you
      </h2>
      <p className="mt-1 text-sm text-neutral-600">Something to read when you have a quiet minute.</p>
      {/* SIM-SEAM: "More for you" — community posts, a family story, a guide */}
      <FeatureSlot
        name="dashboard.content"
        className="mt-5 flex flex-wrap gap-4 sm:gap-6 [&>:not([data-more-card])]:basis-full"
      />
    </section>
  );
}

function SkeletonGrid() {
  return (
    <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-hidden>
      {[0, 1, 2].map((i) => {
        const accent = ACCENTS[accentForIndex(i)];
        return (
          <li key={i}>
            <Card className={cn("relative h-full overflow-hidden p-5", accent.wash)}>
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-3.5 w-20" />
                </div>
                <Skeleton className="h-12 w-12 rounded-full" />
              </div>
              <Skeleton className="mt-4 h-3 w-24" />
            </Card>
          </li>
        );
      })}
    </ul>
  );
}

/** What goes into a care profile, for the empty state (literal accent names). */
const PROFILE_HOLDS: readonly { icon: LucideIcon; label: string; detail: string; accent: AccentName }[] = [
  {
    icon: ShieldAlert,
    label: "Allergies & medicines",
    detail: "What to avoid, and what to give when",
    accent: "rose",
  },
  {
    icon: PhoneCall,
    label: "Emergency contacts",
    detail: "Who to call first, and how to reach them",
    accent: "amber",
  },
  {
    icon: Clock,
    label: "Routines & meals",
    detail: "Naps, feeding and bedtime, the way they like it",
    accent: "emerald",
  },
  {
    icon: Link2,
    label: "One private link",
    detail: "You choose what each caregiver sees, and can turn it off anytime",
    accent: "violet",
  },
];

/**
 * A fresh account's welcome to profiles: what a care profile is for, what
 * goes in it, and one clear way to start. No clay art here: the hero above
 * already carries the page's one illustration (docs/v2-core/STYLE.md §5).
 */
function NoProfiles({ noun }: { noun: string }) {
  return (
    <m.div
      variants={fadeUp}
      initial="hidden"
      animate="show"
      data-shot="no-profiles"
      className={cn(
        "relative mt-6 overflow-hidden rounded-[2rem] p-5 ring-1 ring-white/70 sm:p-8",
        ACCENTS.sky.wash,
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-rose-200/40 blur-3xl"
      />
      {/* Clear of the rows column on a wide screen. */}
      <Star className="pointer-events-none absolute right-6 top-6 h-5 w-5 text-amber-300 lg:left-[46%] lg:right-auto lg:top-8" />
      <Sparkle className="pointer-events-none absolute bottom-6 left-[46%] hidden h-4 w-4 text-sky-300 lg:block" />
      <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] lg:items-center lg:gap-10">
        <div className="min-w-0">
          <IconChip icon={Heart} accent="rose" size="lg" />
          <h3 className="mt-4 text-balance font-display text-2xl/[1.2] font-semibold text-neutral-900 sm:text-[1.75rem]/[1.2]">
            Start with one care profile
          </h3>
          <p className="mt-2 max-w-xl text-pretty text-[15px]/relaxed text-neutral-700 sm:text-base/relaxed">
            {`A care profile is everything a sitter, grandparent or teacher needs to look after your ${noun}, in one place and always up to date. Start with the nickname they go by, and add the rest whenever you're ready.`}
          </p>
          <div className="mt-5">
            <Button asChild>
              <Link href="/child/new">
                <Plus className="h-4 w-4" aria-hidden />
                {`Add your first ${noun}`}
              </Link>
            </Button>
          </div>
        </div>
        <ul
          aria-label="What a care profile holds"
          className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-1"
        >
          {PROFILE_HOLDS.map(({ icon, label, detail, accent }) => (
            <li key={label} className={cn(ROW, "flex items-center gap-3 p-3 sm:p-3.5")}>
              <IconChip icon={icon} accent={accent} size="sm" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-900">{label}</p>
                <p className="text-xs text-neutral-600">{detail}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </m.div>
  );
}

/** The child card's two small pill actions (literal classes). */
const CARD_ACTION =
  "inline-flex items-center gap-1 rounded-full bg-white/80 px-3 py-1.5 text-sm font-semibold shadow-soft ring-1 ring-white transition";

function ChildCard({ child, index }: { child: ChildSummary; index: number }) {
  const accent = ACCENTS[accentForIndex(index)];
  return (
    <m.li variants={fadeUp} className="h-full">
      {/* A glass tile on its accent wash. "Open profile" is a stretched link
          (its ::after covers the card, so the whole tile still opens the
          profile); Share sits above it as a link of its own. */}
      <Card
        interactive
        className={cn("group relative flex h-full flex-col overflow-hidden p-4 sm:p-5", accent.wash)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-display text-lg font-semibold text-neutral-900">{child.name}</h3>
            <p className="mt-0.5 text-sm text-neutral-700">
              {child.age !== null ? `Age ${child.age}` : "Age not set"}
              {child.grade ? ` · ${child.grade}` : ""}
            </p>
            {child.schoolName && (
              <p className="mt-1 truncate text-xs uppercase tracking-wide text-neutral-700">
                {child.schoolName}
              </p>
            )}
          </div>
          <span
            aria-hidden
            className={cn(
              "inline-flex shrink-0 rounded-full bg-gradient-to-br p-1 shadow-soft",
              accent.gradient,
            )}
          >
            <ChildAvatar
              name={child.name}
              photoUrl={child.photoUrl}
              className="h-11 w-11 ring-2 ring-white"
            />
          </span>
        </div>
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
          <Link
            href={childProfileHref(child.id)}
            aria-label={`Open ${child.name}'s profile`}
            className={cn(
              CARD_ACTION,
              accent.text,
              "after:absolute after:inset-0 after:rounded-[1.75rem] after:content-['']",
              FOCUS_RING,
            )}
          >
            Open profile
            <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" aria-hidden />
          </Link>
          <Link
            href={childShareHref(child.id)}
            aria-label={`Share ${child.name}'s care profile`}
            className={cn(CARD_ACTION, "relative z-10 text-violet-700 hover:bg-white", FOCUS_RING)}
          >
            <Share2 className="h-3.5 w-3.5" aria-hidden />
            Share
          </Link>
        </div>
      </Card>
    </m.li>
  );
}
